<template>
  <div class="app-layout">
    <div v-if="feedStore.isLoading || localLoading" class="loading-overlay">
      <div class="spinner"></div>  
    </div>

    <div class="main-content">
      <!-- Sidebar (left) -->
      <Sidebar 
        :width="sidebarWidth" 
        :hidden="isSidebarHidden"
        @toggle="toggleSidebar"
        @select-node="onFeedNodeSelected"
        ref="sidebarRef"
      />

      <!-- Overlay for mobile/small screens -->
      <div class="sidebar-overlay" v-if="!isSidebarHidden" @click="toggleSidebar"></div>

      <!-- Divider (vertical) -->
      <div class="divider-vertical"
            @mousedown.stop.prevent="e => startResizingVertical(e, $refs.contentAreaRef)"
            @touchstart.stop.prevent="e => startResizingVertical(e, $refs.contentAreaRef)"></div>

      <div class="content-area" ref="contentAreaRef" :class="layoutMode === 'horizontal' ? 'layout-row' : 'layout-column'">
        <!-- Article List (Top/Middle) -->
        <ArticleList 
          :height="layoutMode === 'vertical' ? articleListHeight : null" 
          :width="layoutMode === 'horizontal' ? articleListWidth : null"
          :current-article-id="currentArticle ? currentArticle.idString : null"
          @toggle-sidebar="toggleSidebar"
          @toggle-layout="toggleLayoutMode"
          :layout-mode="layoutMode"
          @select-article="onArticleSelected"
          ref="articleListRef"
        />

        <!-- Divider (horizontal or vertical depending on mode) -->
        <div v-if="layoutMode === 'vertical'" class="divider-horizontal"
              @mousedown.stop.prevent="e => startResizingHorizontal(e, $refs.contentAreaRef)"
              @touchstart.stop.prevent="e => startResizingHorizontal(e, $refs.contentAreaRef)"></div>
        <div v-else class="divider-vertical"
              @mousedown.stop.prevent="e => startResizingArticleList(e, $refs.contentAreaRef)"
              @touchstart.stop.prevent="e => startResizingArticleList(e, $refs.contentAreaRef)"></div>

        <!-- Article Reader (Bottom/Right) -->
        <ArticleReader 
          :article="currentArticle" 
          :height="layoutMode === 'vertical' ? articleSummaryHeight : null"
          @play="playArticle"
          @ai-review="getAIReview"
          @open-original="onOpenOriginal"
          ref="readerRef"
        />
      </div>
    </div>

    <!-- AI Review Overlay -->
    <AIReviewModal 
        :show="showAIReview"
        :content="aiReviewContent"
        :loading="aiReviewLoading"
        @close="closeAIReview"
        @delete="deleteAIReview"
    />
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useFeedStore } from '@/stores/feedStore';
import { useAuthStore } from '@/stores/userStore';
import { useResizer } from '@/composables/useResizer';
import * as apiService from '@/services/apiService.js';

// Components
import Sidebar from './AppSidebar.vue';
import ArticleList from './ArticleList.vue';
import ArticleReader from './ArticleReader.vue';
import AIReviewModal from './AIReviewModal.vue';

// Composables & Stores
const router = useRouter();
const feedStore = useFeedStore();
const authStore = useAuthStore();
const redirectToLogin = () => router.push({ path: '/login', query: { redirect: router.currentRoute.value.fullPath } });
const { 
    sidebarWidth, articleListHeight, articleSummaryHeight, articleListWidth,
    startResizingVertical, startResizingHorizontal, startResizingArticleList, initLayout 
} = useResizer();

// Layout State
const savedLayout = localStorage.getItem('layoutMode');
const layoutMode = ref(savedLayout || 'vertical');

const toggleLayoutMode = () => {
    layoutMode.value = layoutMode.value === 'vertical' ? 'horizontal' : 'vertical';
    localStorage.setItem('layoutMode', layoutMode.value);
    // Give DOM a chance to render, then re-init layout if needed
    setTimeout(() => initLayout(contentAreaRef.value), 50);
};

// State
const isSidebarHidden = ref(false);
const localLoading = ref(false); // For local actions like Play/AI
const currentArticle = ref(null);
const readerRef = ref(null);
const contentAreaRef = ref(null);

// AI Review State
const showAIReview = ref(false);
const aiReviewContent = ref('');
const aiReviewLoading = ref(false);

// Methods
const toggleSidebar = () => {
    isSidebarHidden.value = !isSidebarHidden.value;
};

const onFeedNodeSelected = async (node) => {
    // 1. Set selected node title
    feedStore.selectedNode = node;
    
    // 2. Reset flow
    feedStore.currentPage = 1;
    await feedStore.fetchArticles(node.id, 1);

    // 3. Auto-select first article (Scheme 2)
    if (feedStore.articles && feedStore.articles.length > 0) {
        onArticleSelected(feedStore.articles[0]);
    } else {
        currentArticle.value = null;
    }
};

const onArticleSelected = (article) => {
    currentArticle.value = article;
    // Log open event
    apiService.articleOpen(article.idString, article.rssNodeId, () => {}).catch(e => console.error(e));
};

// eslint-disable-next-line no-unused-vars
const onOpenOriginal = (article) => {
    // Already opened in new tab via href, just log it?
    // Log handled in onArticleSelected usually, or duplicated here.
};

// Play Action
const playArticle = async (article) => {
    if (!authStore.isAuthenticated) return redirectToLogin();
    
    try {
        const response = await apiService.playArticle(article, (v) => localLoading.value = v, authStore.token);
        if (response && response.url && readerRef.value) {
            readerRef.value.playAudio(response.url);
        }
    } catch (e) {
        console.error("Play failed", e);
        if (e.response?.status === 401) redirectToLogin();
    }
};

// AI Review Action
const getAIReview = async (article) => {
    if (!article) return;
    if (!authStore.isAuthenticated) return redirectToLogin();
    
    showAIReview.value = true;
    aiReviewLoading.value = true;
    aiReviewContent.value = '';

    try {
        const response = await apiService.getAIReview(article.idString, article.rssNodeId, authStore.token);
        if (response && response.content) {
            aiReviewContent.value = response.content;
        }
    } catch (error) {
        console.error('Error getting AI review:', error);
        if (error.status === 401) {
            authStore.setlogout();
            redirectToLogin();
        } else {
            alert('获取 AI 点评失败');
        }
    } finally {
        aiReviewLoading.value = false;
    }
};

const deleteAIReview = async () => {
    if (!currentArticle.value) return;
    try {
        const params = { IdString: currentArticle.value.idString, RssNodeId: currentArticle.value.rssNodeId };
        await apiService.deleteAIReview(params, authStore.token);
        alert('AI点评已删除');
        closeAIReview();
    } catch (error) {
        console.error('Delete failed:', error);
        alert('删除失败');
    }
};

const closeAIReview = () => {
    showAIReview.value = false;
    aiReviewContent.value = '';
};

// Lifecycle
onMounted(() => {
    feedStore.fetchData();
    // Init layout with delay to ensure DOM
    setTimeout(() => initLayout(contentAreaRef.value), 100);
});
</script>

<style scoped>
/* Reuse existing styles */
@import '@/assets/styles/AppLayout.css';
@import '@/assets/styles/components.css';
</style>